"use client";

import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { Key, Compass, BookOpen, Trophy, CheckCircle } from "lucide-react";

export default function OurVision() {
  const heroAnimation = useScrollAnimation({ delay: 0 });
  const contentAnimation = useScrollAnimation({ delay: 200 });
  const pillarsAnimation = useScrollAnimation({ delay: 400 });

  const pillars = [
    {
      number: "1",
      title: "Accessibility – Everyone deserves access to support",
      description:
        "We've removed the biggest barriers to care: our headset is low-cost, our pricing is affordable (with discounts for students and early adopters), and our platform works on most modern smartphones.",
      icon: <Key className="w-6 h-6" />,
    },
    {
      number: "2",
      title: "Immersion – Healing starts with presence",
      description:
        "Our VR experiences are built for emotional resonance: realistic nature scenes, gentle pacing, ambient sound, and intentional design create a sense of calm that encourages reflection and emotional reset.",
      icon: <Compass className="w-6 h-6" />,
    },
    {
      number: "3",
      title: "Education – Understand your mind, change your life",
      description:
        'Every interaction on AnamVR is rooted in CBT, mindfulness, and psychoeducation. We teach the "why" behind your emotional patterns, giving you tools to make sustainable changes — not just in mood, but in mindset.',
      icon: <BookOpen className="w-6 h-6" />,
    },
    {
      number: "4",
      title: "Gamification – Support that keeps you coming back",
      description:
        "We use game design techniques like progress tracking, streaks, rewards, and narrative journeys to make mental health support enjoyable — encouraging consistency and long-term engagement.",
      icon: <Trophy className="w-6 h-6" />,
    },
  ];

  const pillarAnimations = pillars.map((_, index) =>
    useScrollAnimation({ delay: index * 150 + 500 })
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 md:pt-32 pb-12 sm:pb-16">
        <div
          ref={heroAnimation.ref}
          className={`flex flex-col-reverse mt-20 sm:mt-6 lg:grid lg:grid-cols-3 gap-8 lg:gap-12 items-center transition-all duration-700 ${
            heroAnimation.isVisible ? "animate-slide-up" : "opacity-0"
          }`}
        >
          {/* Text Content */}
          <div className="flex flex-col lg:col-span-2 justify-center w-full order-2 lg:order-1">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#2b2b2b] mb-4 sm:mb-6 leading-tight">
              Our{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-800">
                Vission & Pillars:
              </span>{" "}
              Changing the Way the World Heals
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl">
              We envision a world where technology unlocks immersive solutions
              that empower your wellbeing through engaging and educational
              therapeutic experiences, wherever you are.
            </p>
          </div>

          {/* Chart Visualization */}
          <div className="flex justify-center lg:justify-end w-full order-1 lg:order-2">
            <div className="relative">
              {/* Simple Vision Visualization */}
              <div className="w-64 h-64 rounded-full border-4 border-purple-100 flex items-center justify-center">
                <div className="w-48 h-48 rounded-full border-4 border-purple-200 flex items-center justify-center">
                  <div className="w-32 h-32 rounded-full border-4 border-purple-300 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Core Pillars Section */}
      <div className="max-w-4xl py-20 mx-auto px-6 pb-24">
        <div
          ref={pillarsAnimation.ref}
          className={`transition-all duration-700 ${pillarsAnimation.isVisible ? "animate-slide-up" : "opacity-0"}`}
        >
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-[#2b2b2b] leading-snug">
              We're Guided by{" "}
              <span className="text-purple-600">Four Core Pillars</span>
            </h2>
            <p className="mt-2 text-gray-600 text-lg max-w-2xl mx-auto">
              These principles shape everything we design — from immersive
              experiences to how we empower personal healing journeys.
            </p>
          </div>

          <div className="space-y-12">
            {pillars.map((pillar, index) => {
              const animation = pillarAnimations[index];
              return (
                <div
                  key={index}
                  ref={animation.ref}
                  className={`flex gap-6 items-start transition-opacity duration-700 ease-out ${
                    animation.isVisible ? "animate-slide-up" : "opacity-0"
                  }`}
                >
                  {/* Number Circle */}
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 text-white flex items-center justify-center font-bold text-lg">
                    {pillar.number}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-2xl font-semibold text-[#2b2b2b] mb-4">
                      {pillar.title}
                    </h3>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      {pillar.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
